import { isCancel, select, spinner, text } from "@clack/prompts";
import { spawnSync } from "node:child_process";
import { createHash } from "node:crypto";
import { existsSync, readFileSync, readdirSync } from "node:fs";
import { join, relative, resolve } from "node:path";
import chalk from "chalk";
import { Cli, Openapi, z } from "incur";
import { appsSpec, createWhopFetch, filterSpecByTag } from "../api/index.js";
import {
	createApp,
	createAppBuild,
	createDevAccessToken,
	getApp,
	listAppLogs,
	listApps,
	promoteAppBuild,
	updateAppRoute,
	updateAppSecrets,
	uploadBuildArchive,
	waitForBuildPromotion,
	waitForFileReady,
	type WhopApp,
	type WhopAppLog,
} from "./api.js";
import {
	APP_CONFIG_FILENAME,
	findProjectDir,
	readAppConfig,
	slugify,
	writeAppConfig,
	type AppConfig,
} from "./config.js";
import { extractSourceArchive, packSourceArchive } from "./source-archive.js";
import {
	detectPackageManager as detectInstalledPackageManager,
	findViteConfig,
	outputTail,
	pmInstallHint,
	runTanstackCreate,
	tanstackCreateCommand,
	whopifyProject,
	type PackageManager,
} from "./scaffold.js";

// The whop() vite plugin writes this archive into the build output directory.
const BUILD_ARCHIVE = "dist/whop-build.zip";

// Env names that control the local Node/package-manager runtime rather than
// the app. Injecting them from remotely-stored secrets would let anyone with
// developer:update_app on the app execute code on a teammate's machine the
// next time they run `whop apps dev`.
const UNSAFE_DEV_ENV_KEYS = new Set([
	"PATH",
	"NODE_OPTIONS",
	"NODE_EXTRA_CA_CERTS",
	"NODE_REPL_EXTERNAL_MODULE",
]);
const UNSAFE_DEV_ENV_PREFIXES = [
	"NPM_",
	"PNPM_",
	"YARN_",
	"BUN_",
	"LD_",
	"DYLD_",
];

function isUnsafeDevEnvKey(key: string): boolean {
	const upper = key.toUpperCase();
	return (
		UNSAFE_DEV_ENV_KEYS.has(upper) ||
		UNSAFE_DEV_ENV_PREFIXES.some((prefix) => upper.startsWith(prefix))
	);
}

const BuildSchema = z.object({
	id: z.string(),
	status: z.string(),
	is_production: z.boolean().optional(),
	url: z.string().optional(),
});

type ErrorOptions = {
	code: string;
	message: string;
	retryable?: boolean;
	exitCode?: number;
	cta?: {
		description?: string;
		commands: { command: string; description?: string }[];
	};
};

/**
 * incur's `c.error()` builds a result that only takes effect when returned
 * from the command's own `run` — so helper functions abort by throwing this,
 * and `deploy` converts it back into `c.error(...)` at the top level.
 */
class DeployAbort extends Error {
	constructor(readonly options: ErrorOptions) {
		super(options.message);
	}
}

type DeployContext = {
	agent: boolean;
};

/**
 * The project's lockfile decides the package manager; without one (e.g. a
 * fresh scaffold that hasn't installed yet) fall back to whatever is
 * installed, preferring bun — never blindly npm.
 */
function detectProjectPackageManager(projectDir: string): string {
	if (
		existsSync(join(projectDir, "bun.lock")) ||
		existsSync(join(projectDir, "bun.lockb"))
	)
		return "bun";
	if (existsSync(join(projectDir, "pnpm-lock.yaml"))) return "pnpm";
	if (existsSync(join(projectDir, "yarn.lock"))) return "yarn";
	if (existsSync(join(projectDir, "package-lock.json"))) return "npm";
	return detectInstalledPackageManager() ?? "npm";
}

function hasScript(projectDir: string, script: string): boolean {
	try {
		const pkg = JSON.parse(
			readFileSync(join(projectDir, "package.json"), "utf-8"),
		) as { scripts?: Record<string, string> };
		return Boolean(pkg.scripts?.[script]);
	} catch {
		return false;
	}
}

// npm/pnpm/yarn are .cmd shims on Windows, which can't be spawned without a
// shell. Everywhere else, spawning without a shell avoids quoting pitfalls.
const SPAWN_VIA_SHELL = process.platform === "win32";

function runScript(
	projectDir: string,
	script: string,
	extraEnv: Record<string, string> = {},
): void {
	const pm = detectProjectPackageManager(projectDir);
	const result = spawnSync(pm, ["run", script], {
		cwd: projectDir,
		stdio: "inherit",
		shell: SPAWN_VIA_SHELL,
		env: { ...process.env, ...extraEnv },
	});
	if (result.error) {
		throw new Error(
			`Could not run \`${pm} run ${script}\`: ${result.error.message}`,
		);
	}
	if (result.status !== 0) {
		throw new Error(
			`\`${pm} run ${script}\` failed with exit code ${result.status ?? "unknown"}`,
		);
	}
}

function runInstall(c: { agent: boolean }, projectDir: string): void {
	const pm = detectProjectPackageManager(projectDir);
	const spin = c.agent ? null : spinner();
	spin?.start(`Installing dependencies with ${pm}`);
	const result = spawnSync(pm, ["install"], {
		cwd: projectDir,
		stdio: ["ignore", "pipe", "pipe"],
		encoding: "utf-8",
		shell: SPAWN_VIA_SHELL,
	});
	if (result.error || result.status !== 0) {
		spin?.error(`${pm} install failed`);
		throw new Error(
			`\`${pm} install\` failed${result.status != null ? ` with exit code ${result.status}` : ""} — run it manually inside ${projectDir}, then re-run \`whop apps deploy\`.${outputTail(result)}`,
		);
	}
	spin?.stop(`Installed dependencies with ${pm}`);
}

function log(c: { agent: boolean }, message: string): void {
	if (!c.agent) console.log(message);
}

async function promptText(
	message: string,
	placeholder: string,
	initialValue?: string,
): Promise<string | null> {
	const value = await text({ message, placeholder, initialValue });
	if (isCancel(value)) return null;
	return String(value);
}

function cancelled(): never {
	throw new DeployAbort({
		code: "CANCELLED",
		message: "Cancelled.",
		exitCode: 130,
	});
}

function formatManualSteps(steps: string[]): string {
	return steps.map((step, i) => `  ${i + 1}. ${step}`).join("\n");
}

function formatAppList(apps: WhopApp[]): string {
	const recent = [...apps]
		.sort((a, b) => (b.created_at ?? 0) - (a.created_at ?? 0))
		.slice(0, 10);
	return recent
		.map(
			(a) => `  ${a.id}  ${a.name}${a.route ? `  (${a.route}.whop.app)` : ""}`,
		)
		.join("\n");
}

/**
 * Registers the OpenAPI-generated commands for one tag of the apps spec
 * directly onto a command group. Done explicitly (instead of incur's
 * `{ fetch, openapi }` group registration) so generated commands and
 * hand-written ones like `deploy` can live in the same group.
 */
async function registerSpecCommands(
	group: ReturnType<typeof Cli.create>,
	tag: string,
): Promise<void> {
	const spec = filterSpecByTag(appsSpec as Record<string, unknown>, tag);
	const generated = await Openapi.generateCommands(
		spec as Openapi.OpenAPISpec,
		createWhopFetch(),
		{ config: { security: false } },
	);
	for (const [name, entry] of generated) {
		if ("run" in entry) group.command(name, entry);
	}
}

/**
 * Step 1: a package manager must exist before anything else — scaffolding,
 * installing, and building all need one.
 */
function ensurePackageManager(): PackageManager {
	const pm = detectInstalledPackageManager();
	if (pm) return pm;
	throw new DeployAbort({
		code: "NO_PACKAGE_MANAGER",
		message: `No package manager found (looked for bun, pnpm, npm). Install one and re-run \`whop apps deploy\`:\n\n  bun (recommended): ${pmInstallHint("bun")}\n  pnpm:              ${pmInstallHint("pnpm")}\n  npm:               ${pmInstallHint("npm")}`,
	});
}

/**
 * Step 2: the current directory must contain a Vite app (package.json + vite
 * config). Interactively offers to scaffold one; in agent mode prints the
 * exact scaffold command so the agent can run it and re-invoke deploy.
 */
async function ensureViteApp(
	c: DeployContext,
	pm: PackageManager,
	dirOption: string | undefined,
): Promise<{ projectDir: string; freshlyScaffolded: boolean }> {
	const projectDir = dirOption
		? resolve(dirOption)
		: (findProjectDir() ?? process.cwd());

	const isViteApp =
		existsSync(join(projectDir, "package.json")) &&
		findViteConfig(projectDir) !== undefined;
	if (isViteApp) return { projectDir, freshlyScaffolded: false };

	if (c.agent) {
		throw new DeployAbort({
			code: "NOT_A_VITE_APP",
			message: [
				`${projectDir} doesn't look like a Vite app (missing package.json or vite config).`,
				"",
				"Ask the user whether to scaffold a new app here, or locate their existing Vite app.",
				"",
				"To scaffold a new app (TanStack Start), run:",
				`  ${tanstackCreateCommand(pm, "<app-name>")}`,
				`  cd <app-name> && ${pm} install`,
				"",
				"Then re-run `whop apps deploy` from inside the app directory.",
				"Or, if a Vite app already exists somewhere else, re-run `whop apps deploy --dir <path-to-app>`.",
			].join("\n"),
			retryable: true,
			cta: {
				commands: [
					{
						command: "apps deploy",
						description: "Re-run from inside a Vite app directory",
					},
				],
			},
		});
	}

	const choice = await select({
		message: `${projectDir} doesn't look like a Vite app. What do you want to do?`,
		options: [
			{
				value: "scaffold",
				label: "Scaffold a new Whop app here (TanStack Start)",
			},
			{ value: "cancel", label: "Cancel — I'll cd into my app directory" },
		],
	});
	if (isCancel(choice) || choice === "cancel") cancelled();

	const scaffolded = await scaffoldNewApp(c, pm);
	return { projectDir: scaffolded, freshlyScaffolded: true };
}

async function promptForNameAndRoute(): Promise<{
	name: string;
	route: string;
}> {
	const name = await promptText("What is your app called?", "My Site");
	if (name === null) cancelled();

	const slug = slugify(name);
	const routeInput = await promptText(
		"Which route should it live at? (your-route.whop.app)",
		slug,
		slug,
	);
	if (routeInput === null) cancelled();
	const route = slugify(routeInput);
	if (!route) {
		throw new DeployAbort({
			code: "ROUTE_REQUIRED",
			message: `Couldn't derive a route from "${routeInput}" — use letters and numbers (e.g. my-site).`,
			retryable: true,
		});
	}
	return { name, route };
}

/**
 * Interactive scaffold: asks for name + route, registers the app on Whop
 * (claiming the route before any files are written, so a taken route fails
 * fast), scaffolds the TanStack Start template, and links it.
 */
async function scaffoldNewApp(
	c: DeployContext,
	pm: PackageManager,
): Promise<string> {
	const { name, route } = await promptForNameAndRoute();

	const targetDir = resolve(`./${route}`);
	if (existsSync(targetDir) && readdirSync(targetDir).length > 0) {
		throw new DeployAbort({
			code: "TARGET_NOT_EMPTY",
			message: `Target directory is not empty: ${targetDir}. Pick a different route, or run \`whop apps deploy\` from an empty directory.`,
		});
	}

	let app: WhopApp;
	try {
		app = await createApp({ name, route });
	} catch (err) {
		throw new DeployAbort({
			code: "CREATE_FAILED",
			message:
				err instanceof Error ? err.message : "Failed to register the app",
			retryable: true,
		});
	}

	const spin = c.agent ? null : spinner();
	spin?.start("Scaffolding the latest TanStack Start template");
	try {
		runTanstackCreate({ targetDir, projectName: route, pm });
		spin?.stop(`Scaffolded ${route} from the latest TanStack Start template`);
	} catch (err) {
		spin?.error("Scaffold failed");
		throw new DeployAbort({
			code: "SCAFFOLD_FAILED",
			message: `${err instanceof Error ? err.message : "Failed to scaffold the project"}\n\nThe app ${app.id} is already registered — scaffold into a fresh directory and re-run \`whop apps deploy --app ${app.id}\` there.`,
		});
	}

	writeAppConfig(targetDir, { app_id: app.id, name, route });
	return targetDir;
}

/**
 * Step 3b: the project must be linked to a Whop app via whop.app.json.
 * `--app` links explicitly; otherwise offers to select or create one
 * (always creating when the account has no apps yet). In agent mode, prints
 * the exact commands — including recently created apps so the agent can ask
 * the user about reusing one.
 */
async function ensureAppLinked(
	c: DeployContext,
	projectDir: string,
	appOption: string | undefined,
): Promise<AppConfig> {
	if (appOption) {
		let app: WhopApp;
		try {
			app = await getApp(appOption);
		} catch (err) {
			throw new DeployAbort({
				code: "APP_NOT_FOUND",
				message: `Could not load app ${appOption}: ${err instanceof Error ? err.message : "unknown error"}`,
				retryable: true,
			});
		}
		const config = await withRoute(c, app);
		writeAppConfig(projectDir, config);
		log(
			c,
			chalk.dim(
				`Linked ${config.name} (${config.app_id}) → ${APP_CONFIG_FILENAME}`,
			),
		);
		return config;
	}

	const existing = readAppConfig(projectDir);
	if (existing) return existing;

	const apps = await listApps();

	if (c.agent) {
		const reuse =
			apps.length > 0
				? `This account has existing apps — ask the user whether to reuse one:\n${formatAppList(apps)}\n\nTo reuse one:  whop apps deploy --app <app_id>\n\n`
				: "This account has no apps yet, so a new one must be created.\n\n";
		throw new DeployAbort({
			code: "NO_APP_LINKED",
			message: [
				`No ${APP_CONFIG_FILENAME} here — this project isn't linked to a Whop app yet.`,
				"",
				`${reuse}To create a new app, ask the user for a name and route (the route becomes <route>.whop.app), then run:`,
				'  whop apps create --name "My Site" --route my-site',
				"",
				"Then deploy with the returned app id:",
				"  whop apps deploy --app <app_id>",
			].join("\n"),
			retryable: true,
			cta: {
				commands: [
					{
						command: "apps create",
						description: "Create a new app (pass --name and --route)",
					},
					{
						command: "apps deploy",
						description: "Re-run with --app <app_id> to link and deploy",
					},
				],
			},
		});
	}

	let selected: WhopApp | null = null;
	if (apps.length > 0) {
		const recent = [...apps].sort(
			(a, b) => (b.created_at ?? 0) - (a.created_at ?? 0),
		);
		const choice = await select({
			message: "Which app should this project deploy to?",
			options: [
				...recent.map((a) => ({
					value: a.id,
					label: `${a.name} (${a.id}${a.route ? `, ${a.route}.whop.app` : ""})`,
				})),
				{ value: "__new__", label: "Create a new app" },
			],
		});
		if (isCancel(choice)) cancelled();
		if (choice !== "__new__") {
			selected = recent.find((a) => a.id === choice) ?? null;
		}
	}

	if (!selected) {
		const { name, route } = await promptForNameAndRoute();
		try {
			selected = await createApp({ name, route });
		} catch (err) {
			throw new DeployAbort({
				code: "CREATE_FAILED",
				message:
					err instanceof Error ? err.message : "Failed to register the app",
				retryable: true,
			});
		}
	}

	const config = await withRoute(c, selected);
	writeAppConfig(projectDir, config);
	log(
		c,
		chalk.dim(
			`Linked ${config.name} (${config.app_id}) → ${APP_CONFIG_FILENAME}`,
		),
	);
	return config;
}

/**
 * Hosted deploys serve from <route>.whop.app, so a linked app must have a
 * route. Prompts for one interactively; in agent mode prints the exact
 * update command.
 */
async function withRoute(c: DeployContext, app: WhopApp): Promise<AppConfig> {
	if (app.route) return { app_id: app.id, name: app.name, route: app.route };

	if (c.agent) {
		throw new DeployAbort({
			code: "ROUTE_REQUIRED",
			message: `App ${app.id} (${app.name}) has no route — hosted apps are served from <route>.whop.app. Ask the user which route to claim, then run:\n  whop apps update ${app.id} --route <route>\n  whop apps deploy --app ${app.id}`,
			retryable: true,
			cta: {
				commands: [
					{
						command: `apps update ${app.id}`,
						description: "Set the route (pass --route)",
					},
				],
			},
		});
	}

	const slug = slugify(app.name);
	const routeInput = await promptText(
		`${app.name} has no route yet. Which route should it live at? (your-route.whop.app)`,
		slug,
		slug,
	);
	if (routeInput === null) cancelled();
	const route = slugify(routeInput);
	if (!route) {
		throw new DeployAbort({
			code: "ROUTE_REQUIRED",
			message: `Couldn't derive a route from "${routeInput}" — use letters and numbers (e.g. my-site).`,
			retryable: true,
		});
	}
	try {
		const updated = await updateAppRoute(app.id, route);
		return {
			app_id: updated.id,
			name: updated.name,
			route: updated.route ?? route,
		};
	} catch (err) {
		throw new DeployAbort({
			code: "UPDATE_FAILED",
			message: err instanceof Error ? err.message : "Failed to set the route",
			retryable: true,
		});
	}
}

/**
 * `--template` scaffold: clones another app's published source archive into a
 * fresh directory instead of running the TanStack Start template. The new app
 * is registered first (name + route prompted like a normal scaffold, or taken
 * from --app), then the template's production build source archive is
 * downloaded and extracted.
 */
async function scaffoldFromTemplate(
	c: DeployContext,
	templateId: string,
	appOption: string | undefined,
	dirOption: string | undefined,
): Promise<{ projectDir: string; config: AppConfig }> {
	let template: WhopApp;
	try {
		template = await getApp(templateId);
	} catch (err) {
		throw new DeployAbort({
			code: "TEMPLATE_NOT_FOUND",
			message: `Could not load template app ${templateId}: ${err instanceof Error ? err.message : "unknown error"}`,
			retryable: true,
		});
	}
	const sourceUrl = template.production_web_build?.source_url;
	if (!sourceUrl) {
		throw new DeployAbort({
			code: "TEMPLATE_HAS_NO_SOURCE",
			message: `${template.name} (${template.id}) has no published source archive, so it can't be used as a template.`,
		});
	}

	let config: AppConfig;
	if (appOption) {
		let app: WhopApp;
		try {
			app = await getApp(appOption);
		} catch (err) {
			throw new DeployAbort({
				code: "APP_NOT_FOUND",
				message: `Could not load app ${appOption}: ${err instanceof Error ? err.message : "unknown error"}`,
				retryable: true,
			});
		}
		config = await withRoute(c, app);
	} else if (c.agent) {
		throw new DeployAbort({
			code: "TEMPLATE_NEEDS_APP",
			message: [
				"Cloning a template needs a target app. Ask the user for a name and route (the route becomes <route>.whop.app), then run:",
				'  whop apps create --name "My Site" --route my-site',
				"",
				"Then deploy the template into it:",
				`  whop apps deploy --template ${templateId} --app <app_id>`,
			].join("\n"),
			retryable: true,
			cta: {
				commands: [
					{
						command: "apps create",
						description: "Create the new app (pass --name and --route)",
					},
					{
						command: `apps deploy --template ${templateId}`,
						description: "Re-run with --app <app_id> to clone and deploy",
					},
				],
			},
		});
	} else {
		const { name, route } = await promptForNameAndRoute();
		let app: WhopApp;
		try {
			app = await createApp({ name, route });
		} catch (err) {
			throw new DeployAbort({
				code: "CREATE_FAILED",
				message:
					err instanceof Error ? err.message : "Failed to register the app",
				retryable: true,
			});
		}
		config = { app_id: app.id, name, route };
	}

	const targetDir = dirOption
		? resolve(dirOption)
		: resolve(`./${config.route}`);
	if (existsSync(targetDir) && readdirSync(targetDir).length > 0) {
		throw new DeployAbort({
			code: "TARGET_NOT_EMPTY",
			message: `Target directory is not empty: ${targetDir}. Pick a different route or pass --dir <empty-dir>.`,
		});
	}

	const spin = c.agent ? null : spinner();
	spin?.start(`Cloning ${template.name} (${template.id}) source archive`);
	try {
		const response = await fetch(sourceUrl);
		if (!response.ok) {
			throw new Error(
				`Download failed: ${response.status} ${response.statusText}`,
			);
		}
		const zip = new Uint8Array(await response.arrayBuffer());
		const fileCount = extractSourceArchive(zip, targetDir);
		// The template's own link config rides along in its source archive —
		// overwrite it so this project deploys to the new app, not the template.
		writeAppConfig(targetDir, config);
		spin?.stop(
			`Cloned ${fileCount} files from ${template.name} → ${targetDir}`,
		);
	} catch (err) {
		spin?.error("Template clone failed");
		throw new DeployAbort({
			code: "TEMPLATE_CLONE_FAILED",
			message: `${err instanceof Error ? err.message : "Failed to clone the template source"}\n\nThe app ${config.app_id} is already registered — re-run \`whop apps deploy --template ${templateId} --app ${config.app_id}\` to retry.`,
			retryable: true,
		});
	}

	return { projectDir: targetDir, config };
}

function resolveLinkedAppId(appOption: string | undefined): string {
	if (appOption) return appOption;
	const projectDir = findProjectDir();
	const config = projectDir ? readAppConfig(projectDir) : null;
	if (config) return config.app_id;
	throw new Error(
		`No app specified. Pass --app app_xxx or run inside a project with ${APP_CONFIG_FILENAME}.`,
	);
}

// `--since`/`--until` accept a relative duration ("30s", "10m", "2h", "1d")
// or anything the API parses as an ISO 8601 timestamp.
const DURATION_MS: Record<string, number> = {
	s: 1000,
	m: 60_000,
	h: 3_600_000,
	d: 86_400_000,
};

function parseTimeOption(value: string | undefined): string | undefined {
	if (!value) return undefined;
	const match = /^(\d+)([smhd])$/.exec(value);
	if (!match) return value;
	return new Date(
		Date.now() - Number(match[1]) * DURATION_MS[match[2]],
	).toISOString();
}

const LOG_LEVEL_COLORS: Record<string, (s: string) => string> = {
	error: chalk.red,
	warn: chalk.yellow,
};

function printLogLine(row: WhopAppLog): void {
	const time = row.created_at.replace("T", " ").replace("Z", "");
	const color = LOG_LEVEL_COLORS[row.level] ?? chalk.dim;
	const request = row.request_method
		? chalk.dim(` ${row.request_method} ${row.request_path ?? ""}`)
		: "";
	console.log(
		`${chalk.dim(time)} ${color(row.level.padEnd(5))}${request}  ${row.message}`,
	);
	if (row.stack) console.log(chalk.dim(row.stack));
}

function buildSecretsGroup() {
	const secrets = Cli.create("secrets", {
		description:
			"Manage app secrets — encrypted at rest, injected as env bindings into the hosted runtime and `whop apps dev`",
	});

	secrets.command("list", {
		description: "Show the app's secrets",
		options: z.object({
			app: z
				.string()
				.optional()
				.describe("App id (defaults to the project's linked app)"),
		}),
		run: async (c) => {
			const remote = await getApp(resolveLinkedAppId(c.options.app));
			return c.ok({ app_id: remote.id, secrets: remote.secrets ?? {} });
		},
	});

	secrets.command("set", {
		description: "Add or overwrite secrets",
		options: z.object({
			secret: z
				.array(z.string())
				.min(1)
				.describe("KEY=VALUE pair (repeatable)"),
			app: z
				.string()
				.optional()
				.describe("App id (defaults to the project's linked app)"),
		}),
		examples: [
			{ options: { secret: ["MAIL_API_KEY=mail-key-123"] } },
			{ options: { secret: ["A=1", "B=2"], app: "app_xxxxxxxx" } },
		],
		run: async (c) => {
			const updates: Record<string, string> = {};
			for (const pair of c.options.secret) {
				const eq = pair.indexOf("=");
				if (eq <= 0) {
					return c.error({
						code: "INVALID_SECRET",
						message: `"${pair}" is not KEY=VALUE. Use --secret NAME=value (an empty value deletes the secret).`,
					});
				}
				updates[pair.slice(0, eq)] = pair.slice(eq + 1);
			}
			const remote = await updateAppSecrets(
				resolveLinkedAppId(c.options.app),
				updates,
			);
			return c.ok({ app_id: remote.id, secrets: remote.secrets ?? {} });
		},
	});

	secrets.command("unset", {
		description: "Delete secrets by name",
		options: z.object({
			key: z.array(z.string()).min(1).describe("Secret name (repeatable)"),
			app: z
				.string()
				.optional()
				.describe("App id (defaults to the project's linked app)"),
		}),
		examples: [{ options: { key: ["MAIL_API_KEY"] } }],
		run: async (c) => {
			const updates: Record<string, string | null> = {};
			for (const key of c.options.key) updates[key] = null;
			const remote = await updateAppSecrets(
				resolveLinkedAppId(c.options.app),
				updates,
			);
			return c.ok({ app_id: remote.id, secrets: remote.secrets ?? {} });
		},
	});

	return secrets;
}

export async function buildAppGroup() {
	const app = Cli.create("apps", {
		description: "Build and deploy fully-hosted web apps on Whop (*.whop.app)",
	});

	await registerSpecCommands(app, "Apps");

	const builds = Cli.create("builds", {
		description: "Manage app builds (list, inspect, promote)",
	});
	await registerSpecCommands(builds, "App builds");
	app.command(builds);

	app.command(buildSecretsGroup());

	app.command("logs", {
		description:
			"Read the app's server runtime logs — console output, uncaught exceptions, failed requests (kept 7 days)",
		hint: "Newest first in the structured output; the terminal view prints oldest to newest. Deploy, hit your app, then read its logs to debug server code.",
		options: z.object({
			app: z
				.string()
				.optional()
				.describe("App id (defaults to the project's linked app)"),
			level: z
				.enum(["log", "debug", "info", "warn", "error"])
				.optional()
				.describe("Only console lines of this level"),
			query: z
				.string()
				.optional()
				.describe("Only logs whose message contains this text"),
			build: z
				.string()
				.optional()
				.describe("Only logs from this build (abld_xxx)"),
			since: z
				.string()
				.optional()
				.describe(
					'Start of the window — a duration like "10m"/"2h"/"1d" or an ISO 8601 timestamp',
				),
			until: z
				.string()
				.optional()
				.describe("End of the window — a duration or ISO 8601 timestamp"),
			first: z
				.number()
				.optional()
				.describe("Number of log lines to return (max 500)"),
			after: z
				.string()
				.optional()
				.describe("Cursor from a previous page's page_info"),
		}),
		examples: [
			{ description: "Recent logs for the linked app" },
			{
				options: { level: "error" as const, since: "1h" },
				description: "Errors from the last hour",
			},
			{
				options: { query: "checkout", app: "app_xxxxxxxx" },
				description: "Search another app's logs by message text",
			},
		],
		outputPolicy: "agent-only",
		run: async (c) => {
			const appId = resolveLinkedAppId(c.options.app);
			const page = await listAppLogs(appId, {
				level: c.options.level,
				query: c.options.query,
				app_build_id: c.options.build,
				created_after: parseTimeOption(c.options.since),
				created_before: parseTimeOption(c.options.until),
				first: c.options.first,
				after: c.options.after,
			});
			const logs = page.data ?? [];

			if (!c.agent && !c.formatExplicit) {
				if (logs.length === 0) {
					console.log(chalk.dim("No logs in this window."));
				}
				for (const row of [...logs].reverse()) printLogLine(row);
			}

			return c.ok({ app_id: appId, logs, page_info: page.page_info });
		},
	});

	app.command("dev", {
		description: "Run the local dev server for this app",
		hint: "Starts the project's dev script with WHOP_APP_ID set and a short-lived access token injected as WHOP_API_KEY (minted from your CLI credential), so server-side SDK calls work locally without env setup. An explicitly exported WHOP_API_KEY is used as-is.",
		options: z.object({
			dir: z
				.string()
				.optional()
				.describe("Project directory (defaults to the current directory)"),
		}),
		run: async (c) => {
			const projectDir = c.options.dir
				? resolve(c.options.dir)
				: findProjectDir();
			const config = projectDir ? readAppConfig(projectDir) : null;
			if (!projectDir || !config) {
				return c.error({
					code: "NO_PROJECT",
					message: `No ${APP_CONFIG_FILENAME} found in this directory or any parent. Run \`whop apps deploy\` to link this project to a Whop app.`,
					cta: {
						commands: [
							{
								command: "apps deploy",
								description: "Link this project to a Whop app and ship it",
							},
						],
					},
				});
			}

			// Server-side SDK calls read WHOP_API_KEY; mint a short-lived access
			// token from the CLI's credential so they work in local dev with zero
			// env setup. An explicitly exported WHOP_API_KEY wins.
			const injected: Record<string, string> = {
				WHOP_APP_ID: config.app_id,
			};
			if (process.env.WHOP_API_KEY) {
				log(
					c,
					chalk.dim(
						"Using the WHOP_API_KEY from your environment for server-side calls.",
					),
				);
			} else {
				try {
					const minted = await createDevAccessToken();
					injected.WHOP_API_KEY = minted.token;
					const expiresAt =
						typeof minted.expires_at === "number"
							? new Date(minted.expires_at * 1000)
							: minted.expires_at
								? new Date(minted.expires_at)
								: null;
					const expiry = expiresAt
						? ` (expires ${expiresAt.toLocaleTimeString()})`
						: "";
					log(
						c,
						chalk.dim(
							`Injected a temporary access token as WHOP_API_KEY${expiry} — server-side SDK calls just work. Restart \`whop apps dev\` to refresh it.`,
						),
					);
				} catch (err) {
					log(
						c,
						chalk.yellow(
							`Couldn't mint a temporary access token (${err instanceof Error ? err.message : "unknown error"}) — set WHOP_API_KEY manually for server-side calls.`,
						),
					);
				}
			}

			// Mirror the hosted runtime: the app's secrets become env vars for the
			// dev server. Explicitly exported env vars win over stored secrets.
			try {
				const remote = await getApp(config.app_id);
				const secretEntries = Object.entries(remote.secrets ?? {}).filter(
					([key]) => !(key in process.env) && !(key in injected),
				);
				const unsafe = secretEntries.filter(([key]) => isUnsafeDevEnvKey(key));
				for (const [key, value] of secretEntries) {
					if (!isUnsafeDevEnvKey(key)) injected[key] = value;
				}
				if (unsafe.length > 0) {
					log(
						c,
						chalk.yellow(
							`Skipped app secret${unsafe.length === 1 ? "" : "s"} ${unsafe.map(([key]) => key).join(", ")} — these names control the local runtime and are never injected by \`whop apps dev\`.`,
						),
					);
				}
				const injectedCount = secretEntries.length - unsafe.length;
				if (injectedCount > 0) {
					log(
						c,
						chalk.dim(
							`Injected ${injectedCount} app secret${injectedCount === 1 ? "" : "s"} into the environment (${secretEntries
								.filter(([key]) => !isUnsafeDevEnvKey(key))
								.map(([key]) => key)
								.join(", ")}).`,
						),
					);
				}
			} catch (err) {
				log(
					c,
					chalk.yellow(
						`Couldn't fetch app secrets (${err instanceof Error ? err.message : "unknown error"}) — continuing without them.`,
					),
				);
			}

			log(c, chalk.dim(`Starting dev server for ${config.name}...`));
			try {
				runScript(projectDir, "dev", injected);
			} catch (err) {
				return c.error({
					code: "DEV_FAILED",
					message:
						err instanceof Error
							? err.message
							: "Dev server exited with an error",
				});
			}
			return c.ok({ success: true });
		},
	});

	app.command("deploy", {
		description: "Build, upload and ship this app live",
		hint: "The one-stop deploy: verifies the project is a Whop-ready Vite app (offering to scaffold or link one if not), builds, typechecks, uploads the build, and promotes it to production. Use --skip_promote to upload a preview-only build, then promote it later with `whop apps builds promote <build_id>`.",
		options: z.object({
			dir: z
				.string()
				.optional()
				.describe("Project directory (defaults to the current directory)"),
			app: z
				.string()
				.optional()
				.describe(
					"Link this project to an app (app_xxx) before deploying — writes whop.app.json, replacing any existing link",
				),
			template: z
				.string()
				.optional()
				.describe(
					"Create a new app from a template app (app_xxx): clones its published source archive into a fresh directory and deploys it",
				),
			skip_promote: z
				.boolean()
				.optional()
				.describe("Upload the build without promoting it to production"),
			skip_typecheck: z
				.boolean()
				.optional()
				.describe("Skip the typecheck step"),
			skip_build: z
				.boolean()
				.optional()
				.describe("Skip the build step and upload the existing dist/ output"),
		}),
		output: BuildSchema,
		outputPolicy: "agent-only",
		examples: [
			{ description: "Build and ship to production" },
			{
				options: { app: "app_xxxxxxxx" },
				description: "Link this project to an existing app, then deploy",
			},
			{
				options: { template: "app_xxxxxxxx" },
				description:
					"Create a new app from a template app's source and deploy it",
			},
			{
				options: { skip_promote: true },
				description: "Upload a preview build without promoting",
			},
		],
		run: async (c) => {
			try {
				const pm = ensurePackageManager();

				let projectDir: string;
				let freshlyScaffolded: boolean;
				let config: AppConfig;
				if (c.options.template) {
					({ projectDir, config } = await scaffoldFromTemplate(
						c,
						c.options.template,
						c.options.app,
						c.options.dir,
					));
					freshlyScaffolded = true;
				} else {
					({ projectDir, freshlyScaffolded } = await ensureViteApp(
						c,
						pm,
						c.options.dir,
					));
					config = await ensureAppLinked(c, projectDir, c.options.app);
				}

				const { manualSteps } = whopifyProject({
					targetDir: projectDir,
					route: config.route,
				});
				if (manualSteps.length > 0) {
					return c.error({
						code: "WHOPIFY_INCOMPLETE",
						message: `${config.name} is linked (${config.app_id}), but the project isn't fully wired for Whop hosting yet. Complete these steps:\n\n${formatManualSteps(manualSteps)}\n\nThen re-run \`whop apps deploy\` — it re-checks the wiring and applies anything it can.`,
					});
				}

				if (!c.options.skip_build) {
					if (
						freshlyScaffolded ||
						!existsSync(join(projectDir, "node_modules", "@whop", "cli"))
					) {
						// whopifyProject adds @whop/cli as a devDependency (the vite
						// config imports whop() from @whop/cli/vite), so the build needs
						// it installed.
						runInstall(c, projectDir);
					}
					// Build BEFORE typecheck: framework builds generate typed artifacts
					// the typecheck depends on (TanStack's routeTree.gen), so a fresh
					// never-built project would otherwise always fail its first deploy.
					log(c, chalk.dim("[1/4] Building..."));
					runScript(projectDir, "build", { WHOP_APP_ID: config.app_id });
					if (!c.options.skip_typecheck && hasScript(projectDir, "typecheck")) {
						log(c, chalk.dim("[2/4] Typechecking..."));
						runScript(projectDir, "typecheck");
					}
				}

				const archivePath = join(projectDir, BUILD_ARCHIVE);
				if (!existsSync(archivePath)) {
					return c.error({
						code: "NO_BUILD_ARCHIVE",
						message: `No build archive at ${BUILD_ARCHIVE}. Add the whop() plugin from @whop/cli/vite to your vite config, then build. See https://whop.com/docs/apps/hosting.`,
					});
				}

				const zip = readFileSync(archivePath);
				const sizeMb = (zip.byteLength / 1024 / 1024).toFixed(1);
				const checksum = createHash("sha256").update(zip).digest("hex");

				const sourceArchive = packSourceArchive(projectDir);
				if ("skipped" in sourceArchive) {
					log(
						c,
						chalk.yellow(
							`Skipping source archive upload: ${sourceArchive.skipped}`,
						),
					);
				} else if (sourceArchive.note) {
					log(c, chalk.dim(`Source archive: ${sourceArchive.note}`));
				}

				const spin = c.agent ? null : spinner();
				spin?.start(`[3/4] Uploading build archive (${sizeMb} MB)`);
				let build;
				try {
					const fileId = await uploadBuildArchive(
						new Uint8Array(zip),
						`${config.route}-build.zip`,
					);
					let sourceFileId: string | undefined;
					if ("zip" in sourceArchive) {
						const sourceSizeMb = (
							sourceArchive.zip.byteLength /
							1024 /
							1024
						).toFixed(1);
						spin?.message(
							`[3/4] Uploading source archive (${sourceArchive.fileCount} files, ${sourceSizeMb} MB)`,
						);
						sourceFileId = await uploadBuildArchive(
							sourceArchive.zip,
							`${config.route}-source.zip`,
						);
					}
					spin?.message("[3/4] Processing archive");
					await waitForFileReady(fileId);
					if (sourceFileId) await waitForFileReady(sourceFileId);
					spin?.message("[3/4] Creating build");
					build = await createAppBuild({
						app_id: config.app_id,
						checksum,
						file_id: fileId,
						source_file_id: sourceFileId,
					});
					spin?.stop(`[3/4] Build uploaded (${build.id})`);
				} catch (err) {
					spin?.error("[3/4] Upload failed");
					throw err;
				}

				if (!c.options.skip_promote) {
					const promoteSpin = c.agent ? null : spinner();
					promoteSpin?.start("[4/4] Promoting to production");
					try {
						await promoteAppBuild(build.id);
						build = await waitForBuildPromotion(build.id);
						promoteSpin?.stop(
							build.is_production
								? "[4/4] Promoted to production"
								: `[4/4] Promotion pending (status: ${build.status})`,
						);
					} catch (err) {
						promoteSpin?.error("[4/4] Promote failed");
						throw err;
					}
				} else {
					log(c, chalk.dim("[4/4] Skipping promotion (--skip_promote)"));
				}

				const promoted = build.is_production === true;
				const productionUrl = promoted
					? ((await getApp(config.app_id)).hosted_url ?? undefined)
					: undefined;

				if (!c.agent && !c.formatExplicit) {
					const cdTarget = relative(process.cwd(), projectDir) || ".";
					console.log(
						[
							"",
							chalk.green.bold(
								promoted ? "✓ Deployed to production" : "✓ Build uploaded",
							),
							`  ${chalk.dim(build.id)}`,
							"",
							...(promoted && productionUrl
								? [
										`  ${chalk.bold("Live")}  ${chalk.cyan.underline(productionUrl)}`,
									]
								: [
										`${chalk.bold("Promote when ready")}  ${chalk.cyan.bold(`whop apps builds promote ${build.id}`)}`,
									]),
							...(freshlyScaffolded
								? [
										"",
										chalk.bold("Next"),
										...(cdTarget !== "." ? [`  cd ${cdTarget}`] : []),
										`  ${chalk.cyan.bold("whop apps dev")}      ${chalk.dim("→ local dev server")}`,
										`  ${chalk.cyan.bold("whop apps deploy")}   ${chalk.dim("→ ship your changes")}`,
									]
								: []),
							"",
						].join("\n"),
					);
				}

				return c.ok(
					{
						id: build.id,
						status: build.status,
						is_production: promoted,
						url: productionUrl,
					},
					promoted
						? undefined
						: {
								cta: {
									commands: [
										{
											command: `apps builds promote ${build.id}`,
											description: "Promote this build to production",
										},
									],
								},
							},
				);
			} catch (err) {
				if (err instanceof DeployAbort) return c.error(err.options);
				return c.error({
					code: "DEPLOY_FAILED",
					message: err instanceof Error ? err.message : "Deploy failed",
					retryable: true,
				});
			}
		},
	});

	return app;
}
